// FILE: src/admin/pages/support/owners/tabs/SupportOwnerBookingsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getSupportOwnerBookingDetail, listSupportOwnerBookings } from "../../../../../api/adminSupportApi";

/* --------------------- helpers --------------------- */
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x);
}
function upper(x) {
  const s = safeStr(x).trim();
  return s ? s.toUpperCase() : "";
}
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function money(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** Clean date: "7 Feb 2026, 12:12 PM" */
function dtClean(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return safeStr(v);
    const datePart = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    const timePart = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${datePart}, ${timePart}`;
  } catch (_) {
    return safeStr(v);
  }
}
function dtFromParts(dateStr, timeStr) {
  const d = safeStr(dateStr).trim();
  const t = safeStr(timeStr).trim();
  if (!d && !t) return "—";
  if (d.includes("T")) return dtClean(d);
  if (t.includes("T")) return dtClean(t);
  if (d && t) {
    const withSeconds = t.length === 5 ? `${t}:00` : t;
    return dtClean(`${d}T${withSeconds}`);
  }
  return dtClean(d || t);
}

/* --------------------- chips --------------------- */
function Chip({ children, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span className={classNames("inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium", cls, className)}>
      {children}
    </span>
  );
}

function toneForBookingStatus(s) {
  const x = upper(s);
  if (["COMPLETED", "ENDED"].includes(x)) return "good";
  if (["CANCELLED"].includes(x)) return "bad";
  if (["UNFULFILLED", "EXPIRED"].includes(x)) return "warn";
  return "neutral";
}
function toneForPayment(s) {
  const x = upper(s);
  if (["PAID", "SUCCESS", "CAPTURED"].includes(x)) return "good";
  if (["UNPAID", "CREATED", "PENDING"].includes(x)) return "warn";
  if (["FAILED"].includes(x)) return "bad";
  return "neutral";
}
function toneForPayout(s) {
  const x = upper(s);
  if (["SUCCESS", "PAID", "COMPLETED"].includes(x)) return "good";
  if (["PENDING", "QUEUED", "PROCESSING", "INITIATED"].includes(x)) return "warn";
  if (["FAILED", "REVERSED"].includes(x)) return "bad";
  return "neutral";
}

/** Refund status derived from booking row values (paidAmount/refundedAmount) */
function refundStatusFromAmounts(paidAmount, refundedAmount) {
  const paid = num(paidAmount);
  const refunded = num(refundedAmount);
  if (refunded <= 0) return { label: "NO REFUND", tone: "neutral" };
  if (paid > 0 && refunded >= paid) return { label: "REFUNDED FULL", tone: "good" };
  return { label: "REFUNDED PART", tone: "warn" };
}
function toneForRefundLabel(label) {
  const x = upper(label);
  if (x.includes("FULL")) return "good";
  if (x.includes("PART")) return "warn";
  if (x.includes("FAILED")) return "bad";
  return "neutral";
}

/* -------------------- footer (your preferred style) -------------------- */
function Footer({ page, size, total, totalPages, setPage, setSize }) {
  const from = total ? page * size + 1 : 0;
  const to = total ? Math.min(total, (page + 1) * size) : 0;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          {total ? (
            <>
              Showing <span className="font-semibold text-slate-700">{from}</span>–<span className="font-semibold text-slate-700">{to}</span> of{" "}
              <span className="font-semibold text-slate-700">{total}</span>
            </>
          ) : (
            "No records"
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(0);
                }}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 20, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of <span className="font-semibold">{Math.max(1, totalPages || 1)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={page + 1 >= (totalPages || 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------- Drawer ui primitives --------------------- */
function KV({ label, value, mono = false }) {
  const v = safeStr(value) || "—";
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={classNames("mt-1 text-sm text-slate-900", mono ? "tabular-nums" : "")}>{v}</div>
    </div>
  );
}

function Card({ title, right, children, className = "" }) {
  return (
    <div className={classNames("rounded-2xl border border-slate-200 bg-white overflow-hidden", className)}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</div>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function DividerLine() {
  return <div className="h-px bg-slate-200" />;
}

/* --------------------- Drawer --------------------- */
function BookingDrawer({ open, onClose, ownerId, bookingId }) {
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!open || !ownerId || !bookingId) return;
      setBusy(true);
      setErr("");
      setDetail(null);
      try {
        const d = await getSupportOwnerBookingDetail(ownerId, bookingId);
        if (!alive) return;
        setDetail(d || null);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load booking details");
        setDetail(null);
      } finally {
        if (alive) setBusy(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, [open, ownerId, bookingId]);

  if (!open) return null;

  const b = detail?.booking || {};
  const req = detail?.request || null;
  const off = detail?.offer || null;
  const evs = Array.isArray(detail?.requestEvents) ? detail.requestEvents : [];

  const bookingStatus = upper(b?.status || b?.bookingStatus);
  const payStatus = upper(b?.paymentStatus || b?.requestPaymentStatus || req?.paymentStatus);
  const payoutStatus = upper(b?.ownerPayoutStatus);

  const pickup = b?.pickupAt ? dtClean(b.pickupAt) : dtFromParts(b?.pickupDate, b?.pickupTime);
  const drop = b?.dropAt ? dtClean(b.dropAt) : dtFromParts(b?.dropDate, b?.dropTime);

  const pickupLoc = safeStr(b?.pickupLocation) || "—";
  const destLine =
    safeStr(b?.destination) ||
    (Array.isArray(b?.destinations) && b.destinations.length ? b.destinations.join(" • ") : "") ||
    "—";

  const driverLine = b?.selfDrive
    ? "Self-drive"
    : [safeStr(b?.assignedDriverName), safeStr(b?.assignedDriverPhone)].filter(Boolean).join(" • ") ||
      (b?.assignedDriverId != null ? `Driver #${b.assignedDriverId}` : "—");

  const ownerFare = b?.fare;
  const userPaid = b?.paidAmount;
  const refunded = b?.refundedAmount;

  // IDs
  const requestId = b?.requestId ?? req?.requestId;
  const matchId = b?.matchId ?? off?.matchId;

  // Refund chip kept in drawer only
  const refundMeta = refundStatusFromAmounts(userPaid, refunded);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[820px] lg:w-[1040px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">Owner • Booking</div>
            <div className="mt-0.5 text-lg font-extrabold text-slate-900 truncate">Booking #{bookingId}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Chip tone={toneForBookingStatus(bookingStatus)}>{bookingStatus || "—"}</Chip>
              <Chip tone={toneForPayment(payStatus)}>{payStatus || "—"}</Chip>
              <Chip tone={toneForPayout(payoutStatus)}>{payoutStatus || "—"}</Chip>
              <Chip tone={refundMeta.tone}>{refundMeta.label}</Chip>
              {b?.matchBatchNo != null ? <Chip>{`BATCH: ${b.matchBatchNo}`}</Chip> : null}
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            <button className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {busy ? (
            <div className="px-6 py-6 text-sm text-slate-600">Loading…</div>
          ) : err ? (
            <div className="px-6 py-6">
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">{err}</div>
            </div>
          ) : !detail ? (
            <div className="px-6 py-6 text-sm text-slate-600">No detail available.</div>
          ) : (
            <div className="px-6 py-6 space-y-5">
              {/* Summary */}
              <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <KV label="Booking ID" value={b?.bookingId ?? bookingId} mono />
                  <KV label="Vehicle Reg" value={b?.vehicleRegNo || "—"} mono />
                  <KV label="Offer ID" value={matchId != null ? `#${matchId}` : "—"} mono />
                  <KV label="Request ID" value={requestId != null ? `#${requestId}` : "—"} mono />
                </div>
              </div>

              {/* Route & schedule */}
              <Card title="Route & schedule" right={b?.selfDrive ? <Chip>Self-drive</Chip> : <Chip className="max-w-[240px] truncate">{driverLine}</Chip>}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-xs font-semibold text-slate-600">Pickup</div>
                    <div className="mt-2">
                      <div className="text-sm text-slate-900">{pickupLoc}</div>
                      <div className="mt-1 text-xs text-slate-500 tabular-nums">{pickup}</div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-xs font-semibold text-slate-600">Destination</div>
                    <div className="mt-2">
                      <div className="text-sm text-slate-900">{destLine}</div>
                      <div className="mt-1 text-xs text-slate-500 tabular-nums">{drop}</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Money & statuses */}
              <Card title="Money & statuses">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Owner fare</div>
                    <div className="mt-2 text-lg font-extrabold text-slate-900 tabular-nums">{ownerFare == null ? "—" : `₹ ${money(ownerFare)}`}</div>
                    <div className="mt-1 text-xs text-slate-500">Owner payout basis</div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">User payment</div>
                      <Chip tone={toneForPayment(payStatus)}>{payStatus || "—"}</Chip>
                    </div>
                    <div className="mt-2 text-lg font-extrabold text-slate-900 tabular-nums">{userPaid == null ? "—" : `₹ ${money(userPaid)}`}</div>
                    <div className="mt-1 text-xs text-slate-500">Paid by user</div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Refund</div>
                      <Chip tone={refundMeta.tone}>{refundMeta.label}</Chip>
                    </div>
                    <div className="mt-2 text-lg font-extrabold text-slate-900 tabular-nums">{refunded == null ? "—" : `₹ ${money(refunded)}`}</div>
                    <div className="mt-1 text-xs text-slate-500">Refunded to user (if any)</div>
                  </div>
                </div>

                <div className="mt-4">
                  <DividerLine />
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Owner payout</div>
                      <Chip tone={toneForPayout(payoutStatus)}>{payoutStatus || "—"}</Chip>
                    </div>
                    <div className="mt-2 text-sm text-slate-900 tabular-nums">{safeStr(b?.ownerPayoutRef) || "—"}</div>
                    <div className="mt-1 text-xs text-slate-500 tabular-nums">{b?.ownerPayoutAt ? dtClean(b.ownerPayoutAt) : "—"}</div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Batch</div>
                    <div className="mt-2 text-sm text-slate-900 tabular-nums">{b?.matchBatchNo != null ? String(b.matchBatchNo) : "—"}</div>
                    <div className="mt-1 text-xs text-slate-500">Matchmaking batch</div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Ride OTP</div>
                    <div className="mt-2 text-sm text-slate-900 tabular-nums">{safeStr(b?.rideOtp) || "—"}</div>
                    <div className="mt-1 text-xs text-slate-500">{b?.rideOtpVerified == null ? "—" : String(!!b.rideOtpVerified).toUpperCase()}</div>
                  </div>
                </div>
              </Card>

              {/* Timeline */}
              <Card title="Request timeline" right={evs.length ? <Chip>{`${evs.length} events`}</Chip> : null}>
                {evs.length === 0 ? (
                  <div className="text-sm text-slate-600">No request events found.</div>
                ) : (
                  <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                    <div className="w-full overflow-x-auto">
                      <table className="min-w-[920px] w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                            <th className="px-4 py-3 w-[170px]">When</th>
                            <th className="px-4 py-3 w-[200px]">Event</th>
                            <th className="px-4 py-3 w-[190px]">Actor</th>
                            <th className="px-4 py-3">Message</th>
                            <th className="px-4 py-3 w-[190px]">Trace</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {evs.map((e) => (
                            <tr key={e.id} className="text-sm text-slate-900">
                              <td className="px-4 py-3">{dtClean(e.createdAt)}</td>
                              <td className="px-4 py-3">{safeStr(e.eventType) || "—"}</td>
                              <td className="px-4 py-3">
                                {[safeStr(e.actorType), e.actorId != null ? `#${e.actorId}` : ""].filter(Boolean).join(" ") || "—"}
                              </td>
                              <td className="px-4 py-3">{safeStr(e.message) || "—"}</td>
                              <td className="px-4 py-3">{safeStr(e.traceId) || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------- component --------------------- */
export default function SupportOwnerBookingsTab() {
  const { ownerId } = useOutletContext();

  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState(null);

  const abortRef = useRef(null);

  const totalPages = useMemo(() => {
    const t = num(total);
    const s = Math.max(1, num(size));
    return Math.max(1, Math.ceil(t / s));
  }, [total, size]);

  async function load() {
    if (!ownerId) return;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    try {
      const data = await listSupportOwnerBookings(ownerId, { status: status || undefined, q: q || undefined, page, size });
      if (ac.signal.aborted) return;

      const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      setRows(content);
      setTotal(num(data?.totalElements ?? content.length ?? 0));
    } catch (e) {
      if (!ac.signal.aborted) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, status, page, size]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      load();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  function openDrawer(bookingId) {
    setActiveBookingId(bookingId);
    setDrawerOpen(true);
  }
  function closeDrawer() {
    setDrawerOpen(false);
    setActiveBookingId(null);
  }

  // ✅ Columns: Offer ID + Request ID are last 2 columns. (No refund column in table)
  const colgroup = useMemo(
    () => (
      <colgroup>
        <col style={{ width: "160px" }} /> {/* Booking ID */}
        <col style={{ width: "170px" }} /> {/* Booking Status */}
        <col style={{ width: "170px" }} /> {/* User Payment */}
        <col style={{ width: "180px" }} /> {/* Vehicle */}
        <col style={{ width: "190px" }} /> {/* Payout Status */}
        <col style={{ width: "170px" }} /> {/* Owner Fare */}
        <col style={{ width: "170px" }} /> {/* Offer ID (LAST-2) */}
        <col style={{ width: "170px" }} /> {/* Request ID (LAST) */}
      </colgroup>
    ),
    []
  );

  return (
    <div className="min-w-0 space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="text-sm font-extrabold text-slate-900">Bookings</div>
              <div className="mt-1 text-xs text-slate-500">Clean overview. Click a row for full route, schedule, payout, payment, and request linkage.</div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <select
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All booking statuses</option>
                <option value="BOOKED">BOOKED</option>
                <option value="STARTED">STARTED</option>
                <option value="ENDED">ENDED</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="CANCELLED">CANCELLED</option>
                <option value="UNFULFILLED">UNFULFILLED</option>
              </select>

              <input
                className="h-10 w-full sm:w-[380px] rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 placeholder:text-slate-400 outline-none"
                placeholder="Search bookingId / offerId / requestId / regNo / phone"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="min-w-[1410px] w-full table-fixed">
            {colgroup}
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Booking ID</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Booking status</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">User payment</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Vehicle</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Payout status</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Owner fare</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Offer ID</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Request ID</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-sm text-slate-600">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center">
                    <div className="text-sm text-slate-900">No bookings</div>
                    <div className="mt-1 text-xs text-slate-500">This owner has no bookings for current filters.</div>
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const bookingStatus = upper(r?.status);
                  const payStatus = upper(r?.paymentStatus || r?.requestPaymentStatus);
                  const payoutStatus = upper(r?.ownerPayoutStatus || "");

                  return (
                    <tr
                      key={`${safeStr(r?.bookingId) || "b"}-${idx}`}
                      className={classNames("cursor-pointer hover:bg-slate-50", idx % 2 === 0 ? "bg-white" : "bg-slate-50/30")}
                      onClick={() => openDrawer(r?.bookingId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openDrawer(r?.bookingId);
                      }}
                      title="Open booking details"
                    >
                      <td className="px-4 py-4 text-sm text-slate-900 tabular-nums">{r?.bookingId != null ? `#${r.bookingId}` : "—"}</td>

                      <td className="px-4 py-4">
                        <Chip tone={toneForBookingStatus(bookingStatus)}>{bookingStatus || "—"}</Chip>
                      </td>

                      <td className="px-4 py-4">
                        <Chip tone={toneForPayment(payStatus)}>{payStatus || "—"}</Chip>
                      </td>

                      <td className="px-4 py-4 text-sm text-slate-900 tabular-nums">{r?.vehicleRegNo ?? "—"}</td>

                      <td className="px-4 py-4">
                        <Chip tone={toneForPayout(payoutStatus)}>{payoutStatus || "—"}</Chip>
                      </td>

                      <td className="px-4 py-4 text-sm text-slate-900 tabular-nums">{r?.fare != null ? `₹ ${money(r.fare)}` : "—"}</td>

                      {/* ✅ last-2 column: Offer ID */}
                      <td className="px-4 py-4 text-sm text-slate-900 tabular-nums">{r?.matchId != null ? `#${r.matchId}` : "—"}</td>

                      {/* ✅ last column: Request ID */}
                      <td className="px-4 py-4 text-sm text-slate-900 tabular-nums">{r?.requestId != null ? `#${r.requestId}` : "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Footer page={page} size={size} total={total} totalPages={totalPages} setPage={setPage} setSize={setSize} />
      </div>

      <BookingDrawer open={drawerOpen} onClose={closeDrawer} ownerId={ownerId} bookingId={activeBookingId} />
    </div>
  );
}
